import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Pause, Play, Square, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTask, useUpdateTask, useDeleteTask } from '../hooks/useTasks';
import { useInstance, usePauseInstance, useResumeInstance, useStopInstance } from '../hooks/useInstances';
import { InstanceStatus } from '../types/instance';
import InstanceStatusBadge from '../components/InstanceStatusBadge';
import KnowledgeViewer from '../components/KnowledgeViewer';

const STATUSES: { value: string; label: string }[] = [
  { value: 'todo', label: 'Todo' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'paused', label: 'Paused' },
  { value: 'in-review', label: 'In Review' },
  { value: 'complete', label: 'Complete' },
];

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: task, isLoading } = useTask(id);
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();

  const { data: instance } = useInstance(task?.activeInstanceId ?? undefined);
  const pauseInstance = usePauseInstance();
  const resumeInstance = useResumeInstance();
  const stopInstance = useStopInstance();

  const [title, setTitle] = useState('');
  const [instructions, setInstructions] = useState('');
  const [editingInstructions, setEditingInstructions] = useState(false);

  useEffect(() => {
    if (task) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTitle(task.title);
      setInstructions(task.instructions ?? '');
    }
  }, [task]);

  if (isLoading) {
    return <div className="p-8 text-sm text-slate-400">Loading task...</div>;
  }

  if (!task) {
    return <div className="p-8 text-sm text-slate-500">Task not found.</div>;
  }

  const handleTitleBlur = () => {
    if (title !== task.title) {
      updateTask.mutate({ id: task.id, title });
    }
  };

  const handleStatusChange = (status: string) => {
    updateTask.mutate({ id: task.id, status: status as typeof task.status });
  };

  const handleSaveInstructions = () => {
    updateTask.mutate({ id: task.id, instructions });
    setEditingInstructions(false);
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    await deleteTask.mutateAsync(task.id);
    navigate(-1);
  };

  const isRunning = instance?.status === InstanceStatus.RUNNING;
  const isPaused = instance?.status === InstanceStatus.PAUSED;
  const turnPercent = instance
    ? Math.min(100, Math.round((instance.currentTurn / instance.maxTurns) * 100))
    : 0;

  return (
    <div className="p-8">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      <div className="max-w-3xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handleTitleBlur}
            className="flex-1 border-0 bg-transparent text-2xl font-bold text-slate-900 focus:outline-none focus:ring-0"
          />
          <button
            onClick={handleDelete}
            className="shrink-0 rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>

        <div className="mb-6 flex items-center gap-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Status</label>
            <select
              value={task.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            >
              {STATUSES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Agent</label>
            <p className="px-1 text-sm text-slate-600">
              {task.assignedAgentId ?? 'Unassigned'}
            </p>
          </div>
        </div>

        <div className="mb-8">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">Instructions</h2>
            <button
              onClick={() => editingInstructions ? handleSaveInstructions() : setEditingInstructions(true)}
              className="text-sm font-medium text-slate-500 hover:text-slate-700"
            >
              {editingInstructions ? 'Save' : 'Edit'}
            </button>
          </div>
          {editingInstructions ? (
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={10}
              className="w-full rounded-lg border border-slate-300 px-4 py-3 font-mono text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
          ) : instructions ? (
            <div className="prose prose-sm prose-slate max-w-none rounded-lg border border-slate-200 bg-white p-4">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {instructions}
              </ReactMarkdown>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No instructions</p>
          )}
        </div>

        <div className="mb-8">
          <h2 className="mb-3 text-lg font-semibold text-slate-900">Instance</h2>
          {instance ? (
            <div className="rounded-lg border border-slate-200 bg-white p-5">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <InstanceStatusBadge status={instance.status} />
                  <span className="text-sm font-medium text-slate-700">
                    {instance.agent?.name ?? 'Agent'}
                  </span>
                  <span className="text-xs text-slate-400">{instance.model}</span>
                </div>
                <div className="flex items-center gap-1">
                  {isRunning && (
                    <button
                      onClick={() => pauseInstance.mutate(instance.id)}
                      className="rounded p-1.5 text-slate-400 hover:bg-yellow-50 hover:text-yellow-600"
                      title="Pause instance"
                    >
                      <Pause className="h-4 w-4" />
                    </button>
                  )}
                  {isPaused && (
                    <button
                      onClick={() => resumeInstance.mutate({ id: instance.id })}
                      className="rounded p-1.5 text-slate-400 hover:bg-green-50 hover:text-green-600"
                      title="Resume instance"
                    >
                      <Play className="h-4 w-4" />
                    </button>
                  )}
                  {(isRunning || isPaused) && (
                    <button
                      onClick={() => stopInstance.mutate(instance.id)}
                      className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title="Stop instance"
                    >
                      <Square className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>

              <div className="mb-3">
                <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                  <span>Turn {instance.currentTurn} / {instance.maxTurns}</span>
                  <span>{turnPercent}%</span>
                </div>
                <div className="h-2 w-full rounded-full bg-slate-100">
                  <div
                    className="h-2 rounded-full bg-blue-500 transition-all"
                    style={{ width: `${turnPercent}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span>
                  Cost: ${Number(instance.totalCostUsd).toFixed(2)} / ${Number(instance.maxBudgetUsd).toFixed(2)} budget
                </span>
                <span>Tokens: {instance.totalTokens.toLocaleString()}</span>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-3">
                <button
                  onClick={() => navigate(`/instances/${instance.id}`)}
                  className="flex items-center gap-1 text-sm font-medium text-slate-600 hover:text-slate-900"
                >
                  View full instance output
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 p-5 text-center">
              <p className="mb-3 text-sm text-slate-500">No active instance</p>
              {task.projectId && (
                <button
                  onClick={() => navigate(`/projects/${task.projectId}/instances`)}
                  className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
                >
                  Launch Instance
                </button>
              )}
            </div>
          )}
        </div>

        <div>
          <h2 className="mb-3 text-lg font-semibold text-slate-900">Knowledge</h2>
          <KnowledgeViewer knowledge={task.knowledge} />
        </div>
      </div>
    </div>
  );
}
